import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  SearchInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@tellescope/ui";
import { formatPhone } from "@tellescope/utils";
import type { TwilioTFNSearchResult } from "@tellescope/tanstack-query";
import { CapabilityBadges } from "./CapabilityBadges";

export interface BuyTollFreeNumbersViewProps {
  isAdmin: boolean;
  areaCodeQuery: string;
  setAreaCodeQuery: Dispatch<SetStateAction<string>>;
  digitsQuery: string;
  setDigitsQuery: Dispatch<SetStateAction<string>>;
  searchedNumbers: TwilioTFNSearchResult[];
  isSearching: boolean;
  hasSearchError: boolean;
  isPurchasing: boolean;
  onBuy: (phoneNumber: string) => void;
  onBack: () => void;
}

export const BuyTollFreeNumbersView = ({
  isAdmin,
  areaCodeQuery,
  setAreaCodeQuery,
  digitsQuery,
  setDigitsQuery,
  searchedNumbers,
  isSearching,
  hasSearchError,
  isPurchasing,
  onBuy,
  onBack,
}: BuyTollFreeNumbersViewProps) => {
  const { t } = useTranslation("onboarding");

  return (
    <div className="min-h-0 pt-6 pb-4 flex flex-col gap-6 justify-center">
      <h3 className="text-xl font-semibold text-gray-900 leading-none">{t("Available Toll-Free Numbers")}</h3>
      <div className="flex gap-4 items-center">
        <SearchInput
          value={areaCodeQuery}
          onChange={(e) => setAreaCodeQuery(e.target.value)}
          wrapperClassName="bg-white"
          placeholder={t("Area code (e.g., 844)")}
        />
        <SearchInput
          value={digitsQuery}
          onChange={(e) => setDigitsQuery(e.target.value)}
          wrapperClassName="bg-white"
          placeholder={t("Contains digits (e.g., 555)")}
        />
      </div>
      <div className="flex max-h-full flex-col overflow-hidden rounded-md border">
        <div className="flex-none overflow-hidden bg-gray-50">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[22%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isSearching ? <Skeleton className="h-3 w-32 bg-gray-300" /> : t("Phone Number")}
                </TableHead>
                <TableHead className="w-[18%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isSearching ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Locality")}
                </TableHead>
                <TableHead className="w-[15%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isSearching ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Region")}
                </TableHead>
                <TableHead className="w-[27%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isSearching ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Capabilities")}
                </TableHead>
                <TableHead className="w-[18%] py-4 px-6 uppercase text-xs text-muted-foreground">
                  {isSearching ? <Skeleton className="h-3 w-24 bg-gray-300" /> : t("Actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
          </Table>
        </div>
        <div className="min-h-0 overflow-y-auto bg-white">
          <Table className="table-fixed">
            <TableBody>
              {isSearching ? (
                Array.from({ length: 8 }, (_, index) => (
                  <TableRow key={index} className="bg-white">
                    <TableCell className="w-[22%] py-4 px-6">
                      <Skeleton className="h-4 w-32 bg-gray-300" />
                    </TableCell>
                    <TableCell className="w-[18%] py-4 px-6">
                      <Skeleton className="h-4 w-24 bg-gray-300" />
                    </TableCell>
                    <TableCell className="w-[15%] py-4 px-6">
                      <Skeleton className="h-4 w-16 bg-gray-300" />
                    </TableCell>
                    <TableCell className="w-[27%] py-4 px-6">
                      <Skeleton className="h-4 w-32 bg-gray-300" />
                    </TableCell>
                    <TableCell className="w-[18%] py-4 px-6">
                      <Skeleton className="h-4 w-24 bg-gray-300" />
                    </TableCell>
                  </TableRow>
                ))
              ) : !searchedNumbers.length ? (
                <TableRow className="bg-white hover:bg-white">
                  <TableCell
                    colSpan={5}
                    className={cn(
                      "py-8 px-6 text-center text-sm",
                      hasSearchError ? "text-destructive" : "text-muted-foreground"
                    )}
                  >
                    {hasSearchError
                      ? t("Failed to search for available numbers")
                      : t("No available numbers match your search")}
                  </TableCell>
                </TableRow>
              ) : (
                searchedNumbers.map(({ phoneNumber, locality, region, capabilities }) => (
                  <TableRow key={phoneNumber} className="bg-white hover:bg-white">
                    <TableCell className="w-[22%] py-4 px-6 truncate">{formatPhone(phoneNumber)}</TableCell>
                    <TableCell className="w-[18%] py-4 px-6 text-muted-foreground truncate">
                      {locality ?? "-"}
                    </TableCell>
                    <TableCell className="w-[15%] py-4 px-6 text-muted-foreground truncate">{region ?? "-"}</TableCell>
                    <TableCell className="w-[27%] py-4 px-6 space-x-1">
                      <CapabilityBadges capabilities={capabilities} idPrefix={phoneNumber} />
                    </TableCell>
                    <TableCell className="w-[18%] py-4 px-6">
                      <Button disabled={!isAdmin} loading={isPurchasing} onClick={() => onBuy(phoneNumber)}>
                        {t("Purchase")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <Button variant="outline" className="self-start" onClick={onBack}>
        {t("Back to Phone Numbers")}
      </Button>
    </div>
  );
};
